import { unwrap } from "@sagot/api-client";
import { LOGO_ACCEPTED_TYPES } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Hint, Icon, initialsOf, toast } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useRef } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useUploadErrorMessage } from "../../../../lib/upload-errors";
import { useApi } from "../../../../session/session-provider";
import type { OrganizationOut } from "../../shared";
import { logoProblem, uploadLogo } from "./logo-upload";

export { logoProblem };

const ACCEPT = LOGO_ACCEPTED_TYPES.join(",");

interface LogoFieldProps {
  org: OrganizationOut;
  disabled: boolean;
  onSaved: () => Promise<unknown>;
}

/** Logo: presigned upload (POST …/logo → PUT file → PATCH logo_url) or remove (initials fallback). */
export function LogoField({ org, disabled, onSaved }: LogoFieldProps) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const uploadMessage = useUploadErrorMessage();
  const input = useRef<HTMLInputElement>(null);
  const upload = useMutation({
    mutationFn: (file: File) => uploadLogo(api, file),
    onSuccess: async () => {
      toast.success(t("settings.org.logoUploaded"));
      await onSaved();
    },
    onError: (caught) => toast.error(uploadMessage(caught, t("settings.org.logoUploadFailed"))),
  });
  const remove = useMutation({
    mutationFn: () => unwrap(api.PATCH("/v1/settings/organization", { body: { logo_url: null } })),
    onSuccess: async () => {
      toast.success(t("settings.org.logoRemoved"));
      await onSaved();
    },
    onError: (caught) => toast.error(message(caught)),
  });
  const pick = (file: File | undefined) => {
    if (!file) return;
    const problem = logoProblem(file);
    if (problem) {
      toast.error(t(problem === "type" ? "settings.org.logoBadType" : "settings.org.logoTooLarge"));
      return;
    }
    upload.mutate(file);
  };
  return (
    <div className="flex items-center gap-4">
      {org.logo_url ? (
        <img src={org.logo_url} alt={t("settings.org.logoAlt", { name: org.name })} className="size-16 rounded-lg border border-line object-cover" />
      ) : (
        <span className="inline-flex size-16 items-center justify-center rounded-lg bg-signal-500 text-xl font-extrabold text-ink-950" aria-hidden="true">
          {initialsOf(org.name)}
        </span>
      )}
      <div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" disabled={disabled} loading={upload.isPending} onClick={() => input.current?.click()}>
            <Icon name="upload" />
            {t("settings.org.uploadLogo")}
          </Button>
          {org.logo_url ? (
            <Button variant="ghost" size="sm" disabled={disabled} loading={remove.isPending} onClick={() => { remove.mutate(); }}>
              {t("settings.org.removeLogo")}
            </Button>
          ) : null}
        </div>
        <Hint>{t("settings.org.logoHint")}</Hint>
        <input
          ref={input}
          type="file"
          accept={ACCEPT}
          className="sr-only"
          tabIndex={-1}
          aria-label={t("settings.org.uploadLogo")}
          onChange={(event) => {
            pick(event.target.files?.[0]);
            event.target.value = "";
          }}
        />
      </div>
    </div>
  );
}
